import { Field } from '@chakra-ui/react';
import { IContactLengthPeriodPopoverSchema } from '@monetize/types/app';
import { getDayOptions, getMonthOptions } from '@monetize/utils/core';
import isNull from 'lodash/isNull';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MBox, MDivider, MFormField, MSwitch, MText } from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';

interface QuoteContractRenewalTermsActionV2Props {
  isDisabled: boolean;
  isAmendmentOrRenewal: boolean;
  isReadOnly?: boolean;
}
export const QuoteAnchorBillingDay = ({
  isDisabled = false,
  isAmendmentOrRenewal,
  isReadOnly = false,
}: QuoteContractRenewalTermsActionV2Props) => {
  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext<IContactLengthPeriodPopoverSchema>();

  const billingMonth = watch('billingAnchor.month');
  const billingDay = watch('billingAnchor.day');
  const monthOptions = getMonthOptions();

  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    // If the billing day or month is set, make it editable
    if (!isNull(billingDay) && !isNull(billingMonth)) {
      setIsEditable(true);
    }
  }, [billingDay, billingMonth]);

  return (
    <>
      <MFormField
        mt="2"
        mb="2"
        display="flex"
        justifyContent="space-between"
        label="Customize Billing Start Date"
        tooltip="The billing anchor sets the day of the month and month of the year you want billing cycles to align with."
      >
        <MSwitch
          size="md"
          isDisabled={isReadOnly || isAmendmentOrRenewal || isDisabled}
          isChecked={isEditable}
          onChange={(event) => {
            if (isEditable && !event.target.checked) {
              setValue('billingAnchor.day', null, { shouldDirty: true });
              setValue('billingAnchor.month', null, { shouldDirty: true });
            } else {
              setValue('billingAnchor.day', billingDay ?? null, {
                shouldDirty: true,
              });
              setValue('billingAnchor.month', billingMonth ?? null, {
                shouldDirty: true,
              });
            }

            setIsEditable(event.target.checked);
          }}
        />
      </MFormField>

      <MText>
        Choose a month and day to anchor billing periods. This determines how
        the first billing cycle is calculated based on your contract start date
        and billing frequency.
      </MText>

      {isEditable && (
        <>
          <MDivider></MDivider>

          <MBox display={'flex'} alignItems="top" gap={2}>
            <MFormField
              mt="2"
              mb="4"
              error={errors.billingAnchor?.month}
              label="Month"
            >
              <Controller
                name="billingAnchor.month"
                control={control}
                render={({ field: { name, ...rest } }) => (
                  <Field.Root
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    disabled={!isEditable || isAmendmentOrRenewal || isDisabled}
                  >
                    <MCustomSelect
                      {...rest}
                      items={monthOptions}
                      itemTitle="title"
                      itemValue="value"
                      placeholder="Select Month"
                      popOverProps={{
                        positioning: {
                          placement: 'bottom-end',
                          sameWidth: false,
                        },
                      }}
                      popOverContentProps={{ minWidth: '10rem' }}
                      isDisabled={
                        !isEditable || isAmendmentOrRenewal || isDisabled
                      }
                      onChange={(value) => {
                        const currentDay = getValues('billingAnchor.day');
                        const daysInMonth = getDayOptions(value).length;

                        if (currentDay && currentDay > daysInMonth) {
                          setValue('billingAnchor.day', null, {
                            shouldDirty: true,
                          });
                        }
                        setValue('billingAnchor.month', value, {
                          shouldDirty: true,
                        });
                      }}
                    />
                  </Field.Root>
                )}
              />
            </MFormField>

            <MFormField
              mt="2"
              mb="4"
              error={errors.billingAnchor?.day}
              label="Day"
            >
              <Controller
                name="billingAnchor.day"
                control={control}
                render={({ field: { name, ...rest } }) => (
                  <Field.Root
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    disabled={
                      !isEditable ||
                      isAmendmentOrRenewal ||
                      !billingMonth ||
                      isDisabled
                    }
                  >
                    <MCustomSelect
                      {...rest}
                      items={getDayOptions(billingMonth)}
                      itemTitle="title"
                      itemValue="value"
                      placeholder="Select Day"
                      popOverProps={{
                        positioning: {
                          placement: 'bottom-end',
                          sameWidth: false,
                        },
                      }}
                      popOverContentProps={{ minWidth: '10rem' }}
                      isDisabled={
                        !isEditable ||
                        isAmendmentOrRenewal ||
                        !billingMonth ||
                        isDisabled
                      }
                    />
                  </Field.Root>
                )}
              />
            </MFormField>
          </MBox>
        </>
      )}
    </>
  );
};
